import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AlertOutcomeTypes } from 'src/constants/AlertOutcomeTypes';
import { DetectAlertCategory, FallRiskActivity } from 'src/constants/DetectAlertCategories';
import { DeviceTypes } from 'src/constants/DeviceTypes';
import { ResolutionTypes } from 'src/constants/ResolutionTypes';
import {
    TaskSeverityLevels,
    isAlertSeverity,
    isDoorAlertSeverity,
} from 'src/constants/TaskSeverityLevels';
import { ClosedTaskStatuses, OpenTaskStatuses, TaskStatuses } from 'src/constants/TaskStatuses';
import { TaskUpdateTypes } from 'src/constants/TaskUpdateTypes';
import { selectStaffMemberId } from 'src/store/configSlice';
import { Task, TaskUpdate } from 'src/store/sageAdminApi';

const FallRiskActivityByUpdateType: Record<string, FallRiskActivity> = {
    [TaskUpdateTypes.DeviceInteractionStandingDetected]: 'STANDING',
    [TaskUpdateTypes.DeviceInteractionEdgeOfBedDetected]: 'EDGE_OF_BED',
};

interface TaskUpdateSummary {
    /** The fall-risk activity from the most recent activity-specific device interaction */
    latestFallRiskActivity?: FallRiskActivity;
    hasFallDetectedUpdate: boolean;
    /** The earliest review update */
    reviewUpdate?: TaskUpdate;
    /** Manual-entry resolutions from the latest close; earlier closes belong to a re-opened alert */
    resolutionsFromUpdates: Set<string>;
}

/** Derives everything read from the task's update history in a single newest-first pass. */
function summarizeTaskUpdates(updates: TaskUpdate[] = []): TaskUpdateSummary {
    const summary: TaskUpdateSummary = {
        hasFallDetectedUpdate: false,
        resolutionsFromUpdates: new Set<string>(),
    };
    let closesSeen = 0;

    for (let i = updates.length - 1; i >= 0; i--) {
        const update = updates[i];
        switch (update.type) {
            case TaskUpdateTypes.DeviceInteractionFallDetected:
                summary.hasFallDetectedUpdate = true;
                break;
            case TaskUpdateTypes.DeviceInteractionStandingDetected:
            case TaskUpdateTypes.DeviceInteractionEdgeOfBedDetected:
                summary.latestFallRiskActivity ??= FallRiskActivityByUpdateType[update.type];
                break;
            case TaskUpdateTypes.Reviewed:
                summary.reviewUpdate = update;
                break;
            case TaskUpdateTypes.Closed:
                closesSeen += 1;
                break;
            case TaskUpdateTypes.ManualEntry:
                if (closesSeen < 2) {
                    summary.resolutionsFromUpdates.add(update.description!);
                }
                break;
            default:
                break;
        }
    }

    return summary;
}

function parseTimestampMs(timestampMs?: string) {
    const parsed = timestampMs ? parseInt(timestampMs, 10) : NaN;
    return Number.isNaN(parsed) ? undefined : parsed;
}

function useTaskProperties(task: Task) {
    const {
        alertOutcome,
        assignee,
        careEvent,
        careProvided,
        creator,
        id: rid,
        resident,
        severity,
        status,
        unit,
        updates,
    } = task || {};

    const currentUserProfileId = useSelector(selectStaffMemberId);
    const { latestFallRiskActivity, hasFallDetectedUpdate, reviewUpdate, resolutionsFromUpdates } =
        useMemo(() => summarizeTaskUpdates(updates), [updates]);

    // Status
    const isMock = rid?.startsWith('ri.mock');
    const isOpen = OpenTaskStatuses.includes(status || '');
    const isClosed = ClosedTaskStatuses.includes(status || '');
    const isClaimed = status === TaskStatuses.OpenClaimed.id;
    const isConfirming = status === TaskStatuses.OpenConfirming.id;
    const isConfirmed = status === TaskStatuses.OpenConfirmed.id;
    const isResolved = status === TaskStatuses.ClosedResolved.id;
    const isUnresolved = status === TaskStatuses.ClosedUnresolved.id;
    const isReviewed = status === TaskStatuses.ClosedReviewed.id;

    // Assignment
    const isAssignedToMe = assignee?.id === currentUserProfileId;
    const isClaimedByMe = isAssignedToMe && isOpen;
    const isResolvedByMe = isAssignedToMe && isClosed;
    const assigneeName = `${assignee?.firstName || ''} ${assignee?.lastName || ''}`;

    // Creator device
    const creatorDeviceType = creator?.device?.type;
    const isCreatedByDevice = !!creator?.device;
    const isButtonPress = creatorDeviceType === DeviceTypes.Button;
    const isOpenResidentPendantAlert = isButtonPress && !!resident && isOpen;
    const isUnitButtonPress = isButtonPress && !resident && !!unit;
    const isM3ButtonPress = creatorDeviceType === DeviceTypes.M3;
    const isUniversalTransmitterPress = creatorDeviceType === DeviceTypes.UniversalTransmitter;
    const isFallDetectionDevice = creatorDeviceType === DeviceTypes.FallDetectionDevice;

    // Alert kind
    const isEmergency = isAlertSeverity(severity);
    // Door-device creator covers door alarms raised before the dedicated door severities existed
    const isDoorAlarm =
        isDoorAlertSeverity(severity) || creatorDeviceType === DeviceTypes.DoorAlarm;
    // Proactive fall risk alerts share the fall-detection creator, so severity is what tells them apart
    const isProactiveFallRiskAlert = severity === TaskSeverityLevels.ProactiveFallRisk.id;
    // Any open unit alert absorbs activity events, so only a proactive alert is described by one
    const fallRiskActivity = isProactiveFallRiskAlert ? latestFallRiskActivity : undefined;
    // Falls are still EMERGENCY alerts created by a fall detection device; a detected-fall update also
    // covers alerts raised some other way that absorbed a fall
    const isPotentialFallAlert =
        severity === TaskSeverityLevels.PotentialFall.id ||
        isFallDetectionDevice ||
        hasFallDetectedUpdate;
    // Proactive fall risk alerts supersede potential fall alerts here because the check is more specific
    const detectAlertCategory: DetectAlertCategory | undefined = isProactiveFallRiskAlert
        ? 'PROACTIVE_FALL_RISK'
        : isPotentialFallAlert
        ? 'POTENTIAL_FALL'
        : undefined;
    const isDetectAlert = !!detectAlertCategory;
    const isOpenProactiveAlert = isOpen && isProactiveFallRiskAlert;
    const isOpenDetectAlert = isOpen && isDetectAlert;

    // Resolution & review
    const resolutionTypes = useMemo(
        () =>
            careEvent?.careOutcomes
                ? new Set(careEvent.careOutcomes.map((careOutcome) => careOutcome.careProvided))
                : resolutionsFromUpdates,
        [careEvent, resolutionsFromUpdates]
    );
    const isFalseAlarm =
        alertOutcome?.type === AlertOutcomeTypes.FalseAlarm ||
        (isClosed && resolutionTypes.has(ResolutionTypes.NoCareGiven.id));
    const isResolvedWithFall =
        (isClosed && resolutionTypes.has(ResolutionTypes.Fall.id)) ||
        (isReviewed && alertOutcome?.type === AlertOutcomeTypes.ResidentInteraction);
    // A caregiver-reported fall (resolution or care event) or a RESIDENT_INTERACTION review outcome;
    // a FALSE_ALARM review overrides a caregiver-reported fall
    const isReportedFall =
        alertOutcome?.type === AlertOutcomeTypes.ResidentInteraction ||
        (alertOutcome?.type !== AlertOutcomeTypes.FalseAlarm &&
            (!!careProvided?.includes(ResolutionTypes.Fall.id) ||
                !!careEvent?.careOutcomes?.some(
                    (outcome) => outcome.careProvided === ResolutionTypes.Fall.id
                )));
    const fallReviewer = isReviewed ? reviewUpdate?.author?.staffMember : undefined;
    const fallReviewedAtMs = isReviewed
        ? parseTimestampMs(reviewUpdate?.creationTimestampInMs)
        : undefined;

    return {
        // Status
        isMock,
        isOpen,
        isClosed,
        isClaimed,
        isConfirming,
        isConfirmed,
        isResolved,
        isUnresolved,
        isReviewed,

        // Assignment
        isAssignedToMe,
        isClaimedByMe,
        isResolvedByMe,
        assigneeName,

        // Creator device
        isCreatedByDevice,
        isButtonPress,
        isOpenResidentPendantAlert,
        isUnitButtonPress,
        isM3ButtonPress,
        isUniversalTransmitterPress,
        isFallDetectionDevice,

        // Alert kind
        isEmergency,
        isDoorAlarm,
        isProactiveFallRiskAlert,
        fallRiskActivity,
        hasFallDetectedUpdate,
        detectAlertCategory,
        isDetectAlert,
        isOpenProactiveAlert,
        isOpenDetectAlert,

        // Resolution & review
        resolutionTypes,
        isFalseAlarm,
        isResolvedWithFall,
        isReportedFall,
        fallReviewer,
        fallReviewedAtMs,
    };
}

export default useTaskProperties;
